<template>
  <div
    class="play-collapse"
    :class="`play-collapse--${type}`"
    role="tablist"
  >
    <slot />
  </div>
</template>

<script>
/**
 * PlayCollapse — accordion/collapse container for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces the
 * legacy @core <AppCollapse> + bootstrap-vue <b-collapse>/<b-card> stack. Holds
 * <PlayCollapseItem> children; `accordion` keeps only one open at a time,
 * `hover` opens items on pointer-enter. `type` switches the visual grouping
 * (default = joined list, border = bordered cards, margin = spaced cards).
 *
 * Coordination uses provide/inject (NOT $parent) so it is Vue 3-safe: items
 * register themselves and ask the group to close siblings in accordion mode.
 *
 * VUE3-MIGRATION: provide/inject + props/slots are already Vue 3-compatible.
 * The provided `api` object is created in data() so Vue 2's reactivity tracks
 * the items array; in Vue 3 this keeps working (reactive object in setup-less
 * Options API). No Vue 2-only constructs here.
 */
let groupUid = 0

export default {
  name: 'PlayCollapse',
  provide() {
    return {
      playCollapse: this.api,
    }
  },
  props: {
    /** Only one item open at a time */
    accordion: {
      type: Boolean,
      default: false,
    },
    /** Open items on pointer-enter, close on leave */
    hover: {
      type: Boolean,
      default: false,
    },
    /** Visual grouping */
    type: {
      type: String,
      default: 'default',
      validator: v => ['default', 'border', 'margin'].includes(v),
    },
  },
  data() {
    groupUid += 1
    return {
      groupId: `play-collapse-${groupUid}`,
      items: [],
      // Shared contract handed to descendants via inject.
      api: {
        accordion: this.accordion,
        hover: this.hover,
        register: this.register,
        unregister: this.unregister,
        requestOpen: this.requestOpen,
      },
    }
  },
  methods: {
    register(item) {
      this.items.push(item)
    },
    unregister(item) {
      this.items = this.items.filter(i => i !== item)
    },
    /** In accordion mode, close every sibling of the item that just opened */
    requestOpen(item) {
      if (!this.accordion) return
      this.items.forEach(i => {
        if (i !== item) i.close()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.play-collapse {
  /* ── default: joined list inside one bordered surface ── */
  &--default {
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-hard);
    overflow: hidden;

    ::v-deep .play-collapse-item + .play-collapse-item {
      border-top: var(--border-w) solid var(--border-ink);
    }
  }

  /* ── border: each item is its own bordered card, stacked tight ── */
  &--border {
    display: flex;
    flex-direction: column;
    gap: 12px;

    ::v-deep .play-collapse-item {
      background: var(--surface-card);
      border: var(--border-w) solid var(--border-ink);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-hard-sm);
      overflow: hidden;
    }
  }

  /* ── margin: roomy spaced cards ── */
  &--margin {
    display: flex;
    flex-direction: column;
    gap: 16px;

    ::v-deep .play-collapse-item {
      background: var(--surface-card);
      border: var(--border-w) solid var(--border-ink);
      border-radius: var(--radius-xl);
      box-shadow: var(--shadow-hard);
      overflow: hidden;
      transition: box-shadow var(--dur-fast) var(--ease-out);
    }

    ::v-deep .play-collapse-item.is-open {
      box-shadow: var(--shadow-hard-lg);
    }
  }
}
</style>
